import {
  SET_PLAYING,
  SET_CORRECT_SOUNDS_PLAYED,
  SET_INCORRECT_SOUNDS_PLAYED,
} from 'actions/types';
import { audioURL } from 'config';
import _ from "lodash";
import getLocaleAudioFolderName from 'utils/getLocaleAudioFolderName';


const filter = 'audio';

const NUMBER_OF_CORRECT_SOUNDS = 7;
const NUMBER_OF_INCORRECT_SOUNDS = 6;
const correctSoundStrings = [1392,1393,1394,1395,1396,1397,1398];
const incorrectSoundStrings = [635,1400,1401,1402,1403,1404];

const reportAudioRequest = (operation, src) => {
  if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;

  const urls = (Array.isArray(src) ? src : [src]).filter(Boolean);

  // Keep diagnostics outside the playback call stack so it can never block audio.
  window.setTimeout(() => {
    window.dispatchEvent(new window.CustomEvent('flcMobileAudioDebug', {
      detail: {
        operation,
        urls,
        timestamp: new Date().toISOString(),
      },
    }));
  }, 0);
};

export const setPlaying = (audioSrc, isPlaying) => ({
  filter,
  type: SET_PLAYING,
  payload: { audioSrc, isPlaying },
});

export const playAudio = (src, options) => {
  reportAudioRequest('playAudio', src);
  window.audioPlayer.playSrc(src, options);
};

export const toggleSequence = (src, options) => {
  reportAudioRequest('toggleSequence', src);
  window.audioPlayer.toggleSequence(src, options);
};

export const toggleAudio = (src, options) => {
  reportAudioRequest('toggleAudio', src);
  window.audioPlayer.togglePlaySrc(src, options);
};

export const stopAudio = (src) => {
  reportAudioRequest('stopAudio', src);
  window.audioPlayer.stop(src);
};

const setCorrectSoundsPlayedCount = (count) => ({
  filter,
  type: SET_CORRECT_SOUNDS_PLAYED,
  payload: count,
});

const setIncorrectSoundsPlayedCount = (count) => ({
  filter,
  type: SET_INCORRECT_SOUNDS_PLAYED,
  payload: count,
});

const resolvePlaybackLocale = (locales = [], solutionLocale, localeOverride) => {
  const overrideCode = typeof localeOverride === 'string'
    ? localeOverride.trim().toLowerCase()
    : String(localeOverride?.code || '').trim().toLowerCase();
  if (overrideCode) {
    const explicit = _.find(locales, (locale) => String(locale?.code || '').toLowerCase() === overrideCode);
    if (explicit) return explicit;
  }
  return solutionLocale || _.find(locales, { code: 'en' }) || locales?.[0] || { code: 'en', name: 'English' };
};

const getStringAudioPath = (editingActivity, solutionLocale, strings, locales, stringNumber) => {
  let audioPath = '';
  const activityLocale = editingActivity 
    ? (editingActivity.activity.currentLanguageVersion.locale 
        ? editingActivity.activity.currentLanguageVersion.locale : 'en') : 'en';
  const locale = solutionLocale ? solutionLocale : _.find(locales, { code: activityLocale });
  const search = _.find(
    strings,
    (item) => Number(item?.stringNumber) === Number(stringNumber)
  );
  const localizedString = _.get(search?.data, locale?.code);
  const audioFilename = localizedString?.stringAudio || localizedString?.audio;

  if (audioFilename) {
    const localeFolder = getLocaleAudioFolderName(locale);
    audioPath = `${audioURL}/Generic/Strings/${localeFolder}/${audioFilename}`;
  }

  return audioPath;
}

export const playApplause = (forAnimation = false) => async (
  dispatch,
  getState
) => {
  const {
    flinkPlay: { solutionLocale },
  } = getState();

  const localeName = solutionLocale ? solutionLocale.name : 'English';

  const audioFilename = forAnimation ? 'applause1.mp3' : 'applause2.mp3';

  const src = `${audioURL}/Generic/ActivityResults/${localeName}/${audioFilename}`;
  playAudio(src);
};

export const playCorrectSound = (localeOverride = null) => (dispatch, getState) => {
  const {
    audio,
    flinkPlay: { solutionLocale, activityLocale },
    common: { strings, locales },
    flinkMake: { activity: { editingActivity } }
  } = getState();
  const activityLocaleCode =
    editingActivity?.activity?.currentLanguageVersion?.locale
    || activityLocale?.code
    || '';
  const playbackLocale = resolvePlaybackLocale(
    locales,
    solutionLocale,
    localeOverride || activityLocaleCode
  );

  const correctSoundsPlayed =
    NUMBER_OF_CORRECT_SOUNDS === audio.correctSoundsPlayed
      ? 1
      : audio.correctSoundsPlayed + 1;

  const audioPath = getStringAudioPath(
    editingActivity, 
    playbackLocale, 
    strings, 
    locales, 
    correctSoundStrings[correctSoundsPlayed - 1]
    );

  const src = audioPath ? audioPath : `${audioURL}/Generic/Feedback/${
    playbackLocale?.name || 'English'
  }/correct${correctSoundsPlayed}.mp3`;

  playAudio(src);

  dispatch(setCorrectSoundsPlayedCount(correctSoundsPlayed));
};

export const playIncorrectSound = (localeOverride = null) => (dispatch, getState) => {
  const {
    audio,
    flinkPlay: { solutionLocale, activityLocale },
    common: { strings, locales },
    flinkMake: { activity: { editingActivity } }
  } = getState();
  const activityLocaleCode =
    editingActivity?.activity?.currentLanguageVersion?.locale
    || activityLocale?.code
    || '';
  const playbackLocale = resolvePlaybackLocale(
    locales,
    solutionLocale,
    localeOverride || activityLocaleCode
  );
  
  const incorrectSoundsPlayed =
    NUMBER_OF_INCORRECT_SOUNDS === audio.incorrectSoundsPlayed
      ? 1
      : audio.incorrectSoundsPlayed + 1;

  const audioPath = getStringAudioPath(
    editingActivity, 
    playbackLocale, 
    strings, 
    locales, 
    incorrectSoundStrings[incorrectSoundsPlayed - 1]
    );

  const src = audioPath ? audioPath : `${audioURL}/Generic/Feedback/${
    playbackLocale?.name || 'English'
  }/incorrect${incorrectSoundsPlayed}.mp3`;

  playAudio(src);

  dispatch(setIncorrectSoundsPlayedCount(incorrectSoundsPlayed));
};

export const playWatch = () => (dispatch, getState) => {
  const {
    flinkPlay: { solutionLocale, activityLocale },
    common: { strings, locales },
    flinkMake: { activity: { editingActivity } }
  } = getState();
  const playbackLocale = resolvePlaybackLocale(
    locales,
    solutionLocale,
    editingActivity?.activity?.currentLanguageVersion?.locale || activityLocale?.code || ''
  );

  const audioPath = getStringAudioPath(
    editingActivity, 
    playbackLocale, 
    strings, 
    locales, 
    1405
    );

  const src = audioPath ? audioPath : `${audioURL}/Generic/Feedback/${
    playbackLocale?.name || 'English'
  }/watch.mp3`;

  playAudio(src);
};
